import {useEffect, useMemo, useState} from "react";
import {Trash2, UserPlus} from "lucide-react";
import Campaign from "@/campaigns/model/Campaign.ts";
import {CampaignRole} from "@/auth/User.ts";
import {AdminUser, CampaignMember} from "@/admin/users/AdminUser.ts";
import {assignCampaignMember, getCampaignMembers, removeCampaignMember, searchAdminUsers} from "@/api/AdminUserApi.ts";
import {Button} from "@/components/ui/button.tsx";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import {Label} from "@/components/ui/label.tsx";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select.tsx";
import {notifyError} from "@/api/ErrorNotification.ts";

export default function CampaignMembersPanel({campaign}: {campaign: Campaign}) {
    const [members, setMembers] = useState<CampaignMember[]>([]);
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [selectedUserId, setSelectedUserId] = useState("");
    const [role, setRole] = useState<CampaignRole>("PLAYER");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            getCampaignMembers(campaign.id),
            searchAdminUsers({page: 0, size: 100, sort: "username"}),
        ]).then(([loadedMembers, loadedUsers]) => {
            if (!cancelled) {
                setMembers(loadedMembers);
                setUsers(loadedUsers.content);
                setError("");
            }
        }).catch(() => {
            if (!cancelled) setError("Mitglieder konnten nicht geladen werden.");
        });
        return () => { cancelled = true; };
    }, [campaign.id]);

    const availableUsers = useMemo(() => {
        const memberIds = new Set(members.map(member => member.userId));
        return users.filter(user => !memberIds.has(user.id));
    }, [members, users]);

    const updateRole = async (member: CampaignMember, nextRole: CampaignRole) => {
        setBusy(true);
        try {
            const saved = await assignCampaignMember(campaign.id, member.userId, nextRole);
            setMembers(values => values.map(value => value.userId === saved.userId ? saved : value));
        } catch (reason) {
            notifyError(reason, "Die Rolle konnte nicht geändert werden.");
        } finally {
            setBusy(false);
        }
    };

    const addMember = async () => {
        if (!selectedUserId) return;
        setBusy(true);
        try {
            const saved = await assignCampaignMember(campaign.id, selectedUserId, role);
            setMembers(values => [...values, saved].sort((a, b) => a.username.localeCompare(b.username, "de")));
            setSelectedUserId("");
            setError("");
        } catch (reason) {
            notifyError(reason, "Der Benutzer konnte nicht zugeordnet werden.");
        } finally {
            setBusy(false);
        }
    };

    const removeMember = async (member: CampaignMember) => {
        setBusy(true);
        try {
            await removeCampaignMember(campaign.id, member.userId);
            setMembers(values => values.filter(value => value.userId !== member.userId));
        } catch (reason) {
            notifyError(reason, "Die Zuordnung konnte nicht entfernt werden.");
        } finally {
            setBusy(false);
        }
    };

    return <Card className="gap-0 py-0">
        <CardHeader className="border-b py-5">
            <CardTitle>Mitglieder</CardTitle>
            <CardDescription>Lege fest, wer diese Kampagne als Spielleiter oder Spieler öffnen darf.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 py-5">
            {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
            <div className="divide-y divide-border overflow-hidden rounded-lg border border-border">
                {members.length === 0 && <p className="p-4 text-sm text-muted-foreground">Noch keine Mitglieder zugeordnet.</p>}
                {members.map(member => <div key={member.userId} className="flex flex-wrap items-center gap-3 p-3">
                    <span className="min-w-0 grow">
                        <span className="block truncate font-medium">{member.displayName || member.username}</span>
                        <span className="block truncate text-xs text-muted-foreground">{member.email || member.username}</span>
                    </span>
                    <Select value={member.role} disabled={busy}
                            onValueChange={value => void updateRole(member, value as CampaignRole)}>
                        <SelectTrigger size="sm" aria-label={`Rolle von ${member.displayName || member.username}`}>
                            <SelectValue/>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="GAME_MASTER">Spielleiter</SelectItem>
                            <SelectItem value="PLAYER">Spieler</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button type="button" variant="destructive" size="icon-sm" disabled={busy}
                            aria-label={`${member.displayName || member.username} entfernen`}
                            onClick={() => void removeMember(member)}><Trash2/></Button>
                </div>)}
            </div>
            <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-surface/40 p-3">
                <div className="min-w-48 grow space-y-2">
                    <Label htmlFor="campaign-member-user">Benutzer</Label>
                    <Select value={selectedUserId || undefined} onValueChange={setSelectedUserId} disabled={busy}>
                        <SelectTrigger id="campaign-member-user" className="w-full">
                            <SelectValue placeholder="Benutzer auswählen"/>
                        </SelectTrigger>
                        <SelectContent>
                            {availableUsers.map(user => <SelectItem key={user.id} value={user.id}>
                                {user.displayName || user.username}
                            </SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="campaign-member-role">Rolle</Label>
                    <Select value={role} onValueChange={value => setRole(value as CampaignRole)} disabled={busy}>
                        <SelectTrigger id="campaign-member-role" className="w-36"><SelectValue/></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="GAME_MASTER">Spielleiter</SelectItem>
                            <SelectItem value="PLAYER">Spieler</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <Button type="button" disabled={busy || !selectedUserId} onClick={() => void addMember()}>
                    <UserPlus/>Hinzufügen
                </Button>
            </div>
        </CardContent>
    </Card>;
}
